/**
 * The Templates tab's logic (spec 014), kept out of the component so it can be tested.
 *
 * The desk says what each template's row holds and what its text breaks, and judges every save (saveProblems); this
 * file only says it in words. "When it sends" comes from the row and the class alone (TMPL-07) — the rows change only
 * by a migration. Stage 2's editor state is a draft of the subject, the body and the case wordings.
 */
import type {
  LifecycleRow, TemplateDetail, TemplateEdit, TemplateProblem, TemplateRow, TemplateState, TemplateVersion,
} from './api';
import { dayLocal } from './time';

export type StateFilter = 'all' | TemplateState;
export interface TemplateFilter { state: StateFilter; family: string; query: string }
export const ALL: TemplateFilter = { state: 'all', family: '', query: '' };

export const STATE_LABEL: Record<TemplateState, string> = { on: 'On', off: 'Off', none: 'Sent by nothing' };

/** The templates a filter keeps: by state, by family, and a search of the subject and the body (TMPL-03). */
export function filterTemplates(rows: TemplateRow[], f: TemplateFilter): TemplateRow[] {
  const q = f.query.trim().toLowerCase();
  return rows.filter((t) => (f.state === 'all' || t.state === f.state)
    && (!f.family || t.family === f.family)
    && (!q || `${t.subject ?? ''}\n${t.body}`.toLowerCase().includes(q) || `t${t.id}` === q));
}

/** The families present, sorted, for the filter. */
export function families(rows: TemplateRow[]): string[] {
  return [...new Set(rows.map((t) => t.family).filter((f): f is string => !!f))].sort();
}

const days = (n: number) => `${n} day${n === 1 ? '' : 's'}`;

/** When the template is sent, in words, from its row and its class only (TMPL-07). */
export function whenItSends(t: Pick<TemplateRow, 'decides' | 'veto_hours'>, row: LifecycleRow | null): string[] {
  if (!row) return ['Nothing sends it: it has no lifecycle row, so no rule raises it.'];
  const out: string[] = [`In the ${row.family} family.`];
  if (Number(row.enabled) !== 1) out.push('Off: nothing sends it until a migration switches it on.');
  if (row.day_from != null && row.day_to != null) out.push(`Only on trial days ${row.day_from} to ${row.day_to}.`);
  else if (row.day_from != null) out.push(`From trial day ${row.day_from}.`);
  if (row.requires_template_sent != null) {
    out.push(row.requires_days_after
      ? `Only after T${row.requires_template_sent} was sent, and ${days(row.requires_days_after)} after it.`
      : `Only after T${row.requires_template_sent} was sent.`);
  }
  out.push(row.contact_window_days == null
    ? 'Not outreach: it never waits for the contact window, and counts toward no one else\'s.'
    : `Waits until the account has had no other outreach email for ${days(row.contact_window_days)}.`);
  out.push(decidesLine(t.decides, t.veto_hours));
  if (Number(row.suspends_on_send) === 1) out.push('Sending it suspends the account.');
  out.push(row.resend_days != null
    ? `May be sent to the same account again after ${days(row.resend_days)}.`
    : 'Sent to an account once.');
  return out;
}

function decidesLine(decides: TemplateRow['decides'], vetoHours: number | null): string {
  if (decides === 'hold') return 'Held for you: it goes only when you approve it.';
  if (decides === 'veto') return `Sends itself ${vetoHours ?? '?'} hours after it is raised, unless you stop it.`;
  if (decides === 'approve') return 'Waits for your approval.';
  if (decides === 'auto') return 'Sent without asking you.';
  return 'Who decides is not known.';
}

/** Last sent and the counts, saying they include v1's sends seeded at cutover (TMPL-08). */
export function countsLine(t: Pick<TemplateRow, 'last_sent' | 'sent_30d' | 'sent_all'>): string {
  const note = 'counted from the system\'s sent records, including v1\'s sends seeded at cutover';
  if (!t.sent_all) return `Never sent — ${note}.`;
  return `Last sent ${dayLocal(t.last_sent)} · ${t.sent_30d} in the last 30 days · ${t.sent_all} in all — ${note}.`;
}

/** A case named by when the rule chooses it (TMPL-05). */
export function caseLabel(name: string | null): string {
  if (name === null) return '';
  const known: Record<string, string> = {
    not_extended: 'When an extension was offered and not taken',
    extended: 'When the trial was extended and the extension has ended',
  };
  return known[name] ?? `Case: ${name.replace(/_/g, ' ')}`;
}

/** Which account the preview was filled from, and why (TMPL-29). */
export function filledFromLine(f: TemplateDetail['filled_from']): string {
  if (f.source === 'typed') return `Filled from account ${f.account_no}, as typed.`;
  if (f.source === 'last_sent') return `Filled from ${f.account_no}, the account last sent it (${dayLocal(f.sent_at)}).`;
  return 'Filled from a made-up sample account (Sample Business): this template has never been sent.';
}

/** The stored text with a placeholder marked, for the "as stored" view: [text, isPlaceholder] parts. */
export function placeholderParts(text: string): [string, boolean][] {
  return String(text).split(/(\[[A-Z0-9_]+\])/g).filter(Boolean).map((p) => [p, /^\[[A-Z0-9_]+\]$/.test(p)]);
}

// ---- Stage 2: the editor ----

export interface TemplateDraft { subject: string; body: string; cases: Record<string, Record<string, string>> }

/** The editor's starting text: the template as stored, or a version being put back (TMPL-23). */
export function draftFrom(t: Pick<TemplateDetail, 'subject' | 'body' | 'cases'>, v?: TemplateVersion | null): TemplateDraft {
  const src = v ?? t;
  return { subject: src.subject ?? '', body: src.body, cases: structuredClone(v?.cases ?? t.cases ?? {}) };
}

/** Whether the editor holds anything the stored template does not. */
export function draftChanged(t: Pick<TemplateDetail, 'subject' | 'body' | 'cases'>, d: TemplateDraft): boolean {
  return d.subject !== (t.subject ?? '') || d.body !== t.body || JSON.stringify(d.cases) !== JSON.stringify(t.cases ?? {});
}

/** The request body for check and save: cases only on a template that has them. */
export function editOf(d: TemplateDraft): TemplateEdit {
  return Object.keys(d.cases).length ? { subject: d.subject, body: d.body, cases: d.cases } : { subject: d.subject, body: d.body };
}

/** One field per case wording, labelled by when the rule chooses it (TMPL-33). */
export function caseFields(cases: Record<string, Record<string, string>>):
  { name: string; key: string; label: string; value: string }[] {
  return Object.entries(cases).flatMap(([name, vars]) => Object.entries(vars).map(([key, value]) => ({
    name, key, value, label: `${caseLabel(name)} — [${key}]`,
  })));
}

/** The draft with one case wording changed. */
export function withCase(d: TemplateDraft, name: string, key: string, value: string): TemplateDraft {
  return { ...d, cases: { ...d.cases, [name]: { ...d.cases[name], [key]: value } } };
}

/** A version in one line: when, who and why. */
export function versionLine(v: Pick<TemplateVersion, 'created_at' | 'changed_by' | 'reason'>): string {
  return `${dayLocal(v.created_at)} · ${v.changed_by.split('@')[0]} · ${v.reason}`;
}

/** The reason a Put back starts with; he finishes it. */
export function putBackReason(v: Pick<TemplateVersion, 'created_at'>): string {
  return `Put back the version of ${dayLocal(v.created_at)}: `;
}

const RULE_LABEL: Record<string, string> = {
  no_row: 'read-only', broadcast: 'broadcast', empty: 'empty', subject_line: 'subject', cases: 'cases',
  unchanged: 'unchanged', money: 'money', setup: 'setup', placeholder: 'placeholder', day_count: 'day count',
  unexplained_day_count: 'day count', pinned: 'pinned sentence', footer: 'footer', call_us: 'phone or hours',
  fault: 'fault', activation: 'activation', reason: 'reason', restored_from: 'version',
};
export const ruleLabel = (rule: string) => RULE_LABEL[rule] ?? rule.replace(/_/g, ' ');

/** The rules line under the editor (TMPL-13): every problem by rule and words, or that it may be saved. */
export function rulesLine(problems: TemplateProblem[] | null, checking: boolean): string {
  if (problems === null) return checking ? 'Checking…' : '';
  const real = problems.filter((p) => p.rule !== 'unchanged');
  if (!real.length) return problems.length ? 'No changes yet.' : 'Passes every rule — give a reason and save.';
  return `${real.length} rule${real.length === 1 ? '' : 's'} to fix before it can be saved.`;
}
